import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { Button } from '@/components/ui/button';
import { AlertCircle, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useUser();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password
      });

      const { access_token, user } = response.data;
      login(access_token, user);

      if (user.force_password_change) {
        navigate('/change-password');
        toast.info('Debes cambiar tu contraseña temporal');
      } else {
        navigate('/');
        toast.success(`Bienvenido/a, ${user.name}`);
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al iniciar sesión';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{ backgroundColor: '#F5F7F8' }}
    >
      {/* Logo de fondo - institucional discreto */}
      <div 
        className="absolute bottom-12 left-1/2 -translate-x-1/2 pointer-events-none select-none"
        style={{ opacity: 0.20 }}
      >
        <img 
          src="/typsa-logo.png" 
          alt="" 
          className="w-32 h-auto"
          aria-hidden="true"
        />
      </div>

      {/* Tarjeta de login */}
      <div 
        className="w-full max-w-md relative z-10"
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
          padding: '40px 36px'
        }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h1 
            style={{ 
              fontFamily: 'Inter, system-ui, sans-serif',
              fontWeight: 600, 
              color: '#1F2933',
              letterSpacing: '-0.01em'
            }}
          >
            <span style={{ fontSize: '2rem', display: 'block' }}>CRM</span>
            <span style={{ fontSize: '1.25rem', display: 'block', marginTop: '4px' }}>TYPSA Digital Solutions</span>
          </h1>
          <p 
            style={{ 
              fontWeight: 400, 
              fontSize: '0.95rem',
              color: '#6B7280',
              marginTop: '12px'
            }}
          >
            Ingresa tus credenciales para acceder
          </p>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div 
              className="flex items-center gap-2 p-3 text-sm rounded-lg"
              style={{ 
                backgroundColor: '#FEF2F2',
                color: '#DC2626',
                border: '1px solid #FECACA'
              }}
              data-testid="login-error"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          
          <div className="space-y-2">
            <label 
              htmlFor="email" 
              style={{ 
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#374151',
                marginBottom: '6px'
              }}
            >
              Correo electrónico
            </label>
            <input
              id="email"
              type="email"
              placeholder="tu@typsa.es"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email"
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: '#F2F4F6',
                borderRadius: '10px',
                border: 'none',
                outline: 'none',
                fontSize: '0.95rem',
                color: '#1F2933',
                transition: 'box-shadow 0.2s ease'
              }}
              onFocus={(e) => e.target.style.boxShadow = '0 0 0 3px rgba(0, 73, 135, 0.2)'}
              onBlur={(e) => e.target.style.boxShadow = 'none'}
            />
          </div>

          <div className="space-y-2">
            <label 
              htmlFor="password" 
              style={{ 
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#374151',
                marginBottom: '6px'
              }}
            >
              Contraseña
            </label>
            <input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password"
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: '#F2F4F6',
                borderRadius: '10px',
                border: 'none',
                outline: 'none',
                fontSize: '0.95rem',
                color: '#1F2933',
                transition: 'box-shadow 0.2s ease'
              }}
              onFocus={(e) => e.target.style.boxShadow = '0 0 0 3px rgba(0, 73, 135, 0.2)'}
              onBlur={(e) => e.target.style.boxShadow = 'none'}
            />
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            data-testid="login-submit"
            className="w-full mt-2 h-12 text-base font-medium"
            style={{
              backgroundColor: '#004987',
              borderRadius: '10px',
              transition: 'all 0.2s ease',
              border: 'none'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = '#003a70';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#004987';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Iniciando sesión...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <LogIn className="w-5 h-5" />
                Iniciar sesión
              </span>
            )}
          </Button>
        </form>

        {/* Mensaje de contacto */}
        <p 
          className="text-center"
          style={{ 
            marginTop: '20px',
            fontSize: '14px',
            color: '#6B7280'
          }}
        >
          Si no puedes acceder, contacta con el administrador del sistema.
        </p>
      </div>
    </div>
  );
};

export default Login;
